import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  CircularProgress,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
} from "@mui/material";

/**
 * Ingram failed-orders queue. Lists orders whose Ingram auto-fulfilment failed
 * (order.status='ingram_failed') with the reason from the linked ingram_jobs
 * row, and a Retry that re-queues the job for the worker.
 */
function IngramFailedOrders() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [retryingId, setRetryingId] = useState(null);
  const [msg, setMsg] = useState("");
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/admin/orders/ingram-failed`,
        { method: "GET", headers: { "Content-Type": "application/json" }, credentials: "include" }
      );
      if (res.status === 200) {
        const data = await res.json();
        setRows(Array.isArray(data.data) ? data.data : []);
      } else if (res.status === 401) {
        navigate("/login");
      }
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const retry = async (orderId) => {
    setRetryingId(orderId);
    setMsg("");
    try {
      const res = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/admin/orders/${orderId}/retry-ingram`,
        { method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include" }
      );
      const data = await res.json().catch(() => ({}));
      if (res.status === 200) {
        setMsg(`Order #${orderId}: re-queued for the worker.`);
        await load();
      } else {
        setMsg(`Order #${orderId}: ${data.msg || "retry failed"}`);
      }
    } catch (err) {
      setMsg(`Order #${orderId}: ${err.message}`);
    } finally {
      setRetryingId(null);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" sx={{ mb: 1, fontWeight: 600 }}>
        Failed Ingram orders
      </Typography>
      <Typography variant="body2" sx={{ mb: 2, color: "text.secondary" }}>
        Orders whose automatic Ingram fulfilment failed. Fix the cause if needed, then Retry to re-queue.
      </Typography>

      {msg && (
        <Typography variant="body2" sx={{ mb: 2, color: "primary.main" }}>
          {msg}
        </Typography>
      )}

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      ) : rows.length === 0 ? (
        <Typography sx={{ py: 4, color: "text.secondary" }}>
          No failed Ingram orders. 🎉
        </Typography>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Order</TableCell>
                <TableCell>Product</TableCell>
                <TableCell>Workspace</TableCell>
                <TableCell>Action</TableCell>
                <TableCell>Job status</TableCell>
                <TableCell>Reason</TableCell>
                <TableCell align="right">Retry</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((o) => {
                const job = o.ingram_job || {};
                return (
                  <TableRow key={o.id} hover>
                    <TableCell>#{o.id}</TableCell>
                    <TableCell>{o.marketplaceName}</TableCell>
                    <TableCell>{o.work_id}</TableCell>
                    <TableCell>{job.action || "—"}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={job.status || "—"}
                        color={
                          job.status === "queued" || job.status === "running"
                            ? "info"
                            : job.status === "needs_human"
                            ? "warning"
                            : "error"
                        }
                      />
                    </TableCell>
                    <TableCell sx={{ maxWidth: 360, whiteSpace: "normal" }}>
                      {job.error || "—"}
                      {typeof job.attempts === "number" ? ` (attempts: ${job.attempts})` : ""}
                    </TableCell>
                    <TableCell align="right">
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={retryingId === o.id || job.status === "queued" || job.status === "running"}
                        onClick={() => retry(o.id)}
                      >
                        {retryingId === o.id ? "…" : "Retry"}
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}

export default IngramFailedOrders;
